import type { FinancialSituationAccountEntry } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { getFieldLabel } from "./translation-utils";
import { T } from "./type-summary.i18n";
import { formatCurrency } from "./utils";

type TypeSummaryProps = {
  summary: Record<string, { label: string; total: number; monthlyTotal?: number }>;
  total: number;
  showMonthly?: boolean;
  currency: string;
  lang: string;
  /** QL-40 — one read-only row per synced client account. */
  accountRows?: FinancialSituationAccountEntry[];
  accountsSyncedAt?: string | null;
};

export function TypeSummary({
  summary,
  total,
  showMonthly = false,
  currency,
  lang,
  accountRows = [],
  accountsSyncedAt = null,
}: TypeSummaryProps) {
  const { formatNumber, formatDate } = useRegionalFormat();
  const t = useT(T);

  return (
    <div className="bg-muted/50 p-3 rounded-lg space-y-2">
      {Object.entries(summary).length > 0 && (
        <div className="space-y-1">
          {Object.entries(summary).map(([typeId, data]) => (
            <div key={typeId} className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">
                {data.label || getFieldLabel(typeId, lang)}
              </span>
              <span>
                {formatCurrency(data.total, currency, formatNumber)}
                {showMonthly && data.monthlyTotal ? (
                  <span className="text-muted-foreground ml-2">
                    ({formatCurrency(data.monthlyTotal, currency, formatNumber)}
                    {t.perMonth})
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}
      {accountRows.length > 0 && (
        <div className="space-y-1">
          {accountRows.map((account) => (
            <div key={account.accountId} className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                {account.name}
                {account.vendor ? ` (${account.vendor})` : ""}
                <Badge variant="outline" className="text-[10px] px-1 py-0 font-normal">
                  {t.fromAccounts}
                </Badge>
              </span>
              {account.amount !== null ? (
                <span>{formatCurrency(account.amount, currency, formatNumber)}</span>
              ) : (
                <span className="text-muted-foreground" title={t.conversionUnavailable}>
                  {formatCurrency(account.originalAmount, account.originalCurrency, formatNumber)} ⚠
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between items-center pt-2 border-t">
        <span className="font-medium">{t.total}</span>
        <span className="text-lg font-bold">{formatCurrency(total, currency, formatNumber)}</span>
      </div>
      {accountRows.length > 0 && accountsSyncedAt && (
        <p className="text-xs text-muted-foreground text-right">
          {t.accountDataAsOf} {formatDate(accountsSyncedAt)}
        </p>
      )}
    </div>
  );
}
